import { track } from '@amplitude/analytics-browser';
import { Application } from '@features/applications';
import {
  getStatusChipColor,
  getStatusChipLabel,
} from '@features/applications/components/ApplicationListItem/utils';
import ApplicationStatusBadge from '@features/applications/components/ApplicationStatusBadge/ApplicationStatusBadge';
import { useUpdateApplicationRating } from '@features/applications/hooks/useUpdateApplicationRating';
import { useUpdateEnterpriseApplicationRating } from '@features/applications/hooks/useUpdateEnterpriseApplicationRating';
import { SignedTemplate } from '@features/digitalOnboarding';
import { useGetSignedTemplates } from '@features/digitalOnboarding/hooks/useGetSignedTemplates';
import EmployerTerminationModal from '@features/employerTeam/components/EmployerTerminationModal';
import MessagingModal from '@features/messaging/components/MessagingModal/MessagingModal';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CloseIcon from '@mui/icons-material/Close';
import {
  alpha,
  Avatar,
  Box,
  Button,
  Checkbox,
  Grid,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Rating,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { staticFileLocation } from '@server/constants';
import { authContext } from '@staffedupusa/shared-components';
import {
  lightGrey,
  primaryBlue,
  primaryPurple,
  successGreen,
  white,
} from '@styles/colors';
import { bold, medium } from '@styles/fontWeights';
import { amplitudeEventsName } from '@utils/amplitudeEvents';
import { formatTimestampIntoRelativeDate } from '@utils/formatTimestamp';
import { selectedPlanName } from '@utils/pricing';
import dayjs from 'dayjs';
import { Dispatch, FC, SetStateAction, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ApplicationActionTime from '../ApplicationActionTime/ApplicationActionTime';
import MarkInterviewCTAs from '../MarkInterviewCTAs/MarkInterviewCTAs';
import MarkInterviewModal from '../MarkInterviewModal/MarkInterviewModal';

type ApplicationListItemProps = {
  application: Application;
  updateRating?: (id: number, newRating: number) => void;
  selectedApplications: number[];
  setSelectedApplications: Dispatch<SetStateAction<number[]>>;
  setUnselectedApplications: Dispatch<SetStateAction<number[]>>;
  searchValue: string;
  setSelectedApplication: Dispatch<SetStateAction<Application | null>>;
  setShowSelectAll: Dispatch<SetStateAction<boolean>>;
  hideSelect?: boolean;
  disableButtons?: boolean;
  setApplicationData?: React.Dispatch<
    React.SetStateAction<Application[] | null>
  >;
};

const ApplicationListItem: FC<ApplicationListItemProps> = ({
  application,
  selectedApplications,
  setSelectedApplications,
  setUnselectedApplications,
  searchValue,
  setShowSelectAll,
  setSelectedApplication,
  hideSelect = false,
  updateRating,
  disableButtons,
  setApplicationData,
}) => {
  const { authData } = useContext(authContext);
  const selectedPlan = selectedPlanName(authData?.user?.company?.plan || 0);
  const isXlScreen = useMediaQuery('(min-width:1400px)');
  const wrapProperty = isXlScreen ? 'nowrap' : 'none';
  const isCompany = authData?.user?.company;
  const companyPlan = authData?.user?.company?.plan;
  const navigate = useNavigate();
  const [isMessagingModalOpen, setIsMessagingModalOpen] = useState(false);
  const [showTerminateModal, setShowTerminateModal] = useState(false);
  const [showInterviewRespondModal, setShowInterviewRespondModal] = useState<
    'completed' | 'missed' | ''
  >('');

  const { updateApplicationRating } = useUpdateApplicationRating({
    searchValue,
  });
  const { updateEnterpriseApplicationRating } =
    useUpdateEnterpriseApplicationRating({
      searchValue,
    });
  const applicationStatusLabel = getStatusChipLabel(
    application.last_action || 'unattended',
    application,
  );

  const applicationIsHired = applicationStatusLabel.toLowerCase() === 'hired';
  const { signedTemplatesData, isSignedTemplatesLoading } =
    useGetSignedTemplates({
      applicationId: application.id,
      enabled: applicationIsHired,
    });
  const signedTemplates: SignedTemplate[] = signedTemplatesData?.object || [];
  const handleToggleApplication = (applicationId: number) => {
    setShowSelectAll && setShowSelectAll(false);

    setSelectedApplications((prevSelectedApplications) => {
      if (prevSelectedApplications.includes(applicationId)) {
        // Remove from selected and add to unselected
        setUnselectedApplications((prevUnselected) => [
          ...prevUnselected,
          applicationId,
        ]);
        return prevSelectedApplications.filter((id) => id !== applicationId);
      }
      // Remove from unselected and add to selected
      setUnselectedApplications((prevUnselected) =>
        prevUnselected.filter((id) => id !== applicationId),
      );
      return [...prevSelectedApplications, applicationId];
    });
  };

  const handleNavigation = (e: {
    stopPropagation: () => void;
    preventDefault: () => void;
  }) => {
    e.stopPropagation();
    e.preventDefault();
    track(
      companyPlan === 1
        ? amplitudeEventsName.SUCCESSFUL_HIRE_SMARTER_APPLICANTS_PAGE_ONBOARDING_BUTTON
        : amplitudeEventsName.SUCCESSFUL_HIRE_STARTER_APPLICANTS_PAGE_ONBOARDING_BUTTON,
    );
    navigate(`/employer/account#Manage-Billing`, { state: { proPlan: true } });
  };

  let finalInterviewDate = '';

  if (application?.calendar_item) {
    const start = dayjs(application.calendar_item?.dt_start_time);
    const end = dayjs(application.calendar_item?.dt_end_time);
    finalInterviewDate = `${start.format('MMMM DD, YYYY')}`;
  } else if (application?.calendar_block_invite) {
    const { block } = application.calendar_block_invite;
    const { slot } = application.calendar_block_invite;
    const startTime = dayjs(
      slot ? slot.slot_start_time : block?.dt_start_time,
    ).format('MMMM DD, YYYY');
    finalInterviewDate = `${startTime}`;
  }

  const handleInterviewRespond = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    type: 'completed' | 'missed',
  ) => {
    e.stopPropagation();
    e.preventDefault();
    setShowInterviewRespondModal(type);
  };
  return (
    <>
      <ListItem
        key={application.id}
        sx={{
          bgcolor: white,
          borderRadius: 8,
          p: 3,
        }}
        style={{ cursor: 'pointer' }}
        onClick={() => {
          setSelectedApplication(application);
          if (authData?.user?.company) {
            track(
              `${hideSelect ? amplitudeEventsName.ONBOARDING_CLICK_APPLICANT : amplitudeEventsName.APPLICANTS_CLICK_APPLICANT}-${selectedPlan}`,
            );
          }
        }}
      >
        <Grid container>
          <Grid
            item
            xs={12}
            sm={6}
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexDirection={{ xs: 'column', sm: 'row' }}
            textAlign={{ xs: 'center', sm: 'left' }}
          >
            {!hideSelect && (
              <Checkbox
                disabled={disableButtons}
                checked={selectedApplications.includes(application.id)}
                onChange={() => handleToggleApplication(application.id)}
                onClick={(e) => e.stopPropagation()}
                sx={{ borderRadius: '50%' }}
              />
            )}
            <ListItemAvatar sx={{ minWidth: { xs: 0, sm: '56px' } }}>
              <Avatar
                src={(application?.applicant?.photo as string) || ''}
                style={{
                  width: '65px',
                  height: '65px',
                  marginRight: '12px',
                  borderRadius: 5,
                }}
              />
            </ListItemAvatar>
            <ListItemText
              style={{ margin: '0' }}
              disableTypography
              primary={
                <Typography
                  variant="h6"
                  component="p"
                  color="secondary"
                  fontSize="19px"
                  fontWeight="bold"
                  style={{ marginBottom: '2px', wordBreak: 'break-word' }}
                >
                  {application?.applicant?.name}
                </Typography>
              }
              secondary={
                <Stack spacing={1}>
                  <Typography
                    color="text.secondary"
                    style={{
                      fontSize: '14px',
                      lineHeight: '12px',
                    }}
                  >
                    {application.job_listing.s_title}
                    {authData?.user?.enterprise ? (
                      <>
                        <span style={{ marginLeft: '7px', marginRight: '7px' }}>
                          -
                        </span>
                        {application.job_listing.company.s_name}
                      </>
                    ) : null}
                  </Typography>
                  <Stack
                    flexDirection="row"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={1}
                  >
                    {application?.tags && (
                      <Stack flexDirection="row" flexWrap="wrap" gap={1}>
                        {application?.tags?.map(
                          (
                            item: { id: number; tag: string },
                            index: number,
                          ) => {
                            return (
                              <Typography
                                style={{
                                  backgroundColor: lightGrey,
                                  borderRadius: 5,
                                  color: 'black',
                                  padding: '2px 7px',
                                  fontSize: '12px',
                                }}
                              >
                                {item?.tag}
                              </Typography>
                            );
                          },
                        )}
                      </Stack>
                    )}
                    <Rating
                      value={application.rating || null}
                      size="small"
                      onChange={(e, val) => {
                        updateRating &&
                          updateRating(application.id, Number(val));
                        authData.user?.enterprise
                          ? updateEnterpriseApplicationRating({
                              rating: Number(val),
                              application_id: application.id,
                              company_id: application.job_listing.company.id,
                            })
                          : updateApplicationRating({
                              rating: Number(val),
                              application_id: application.id,
                            });
                      }}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ marginLeft: application?.tags ? '2px' : 0 }}
                    />
                  </Stack>
                </Stack>
              }
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={3}
            display="flex"
            justifyContent={{ xs: 'center', sm: 'flex-start' }}
            flexDirection={{ xs: 'column' }}
            alignItems="flex-start"
            pt={1}
          >
            {applicationIsHired &&
            signedTemplates?.length > 0 &&
            signedTemplates.filter((item) => item.file_path).length !==
              signedTemplates?.length ? (
              <Box
                px={2}
                py={1}
                bgcolor={alpha(primaryPurple, 0.2)}
                borderRadius="5px"
              >
                <Typography
                  color={primaryPurple}
                  textTransform="capitalize"
                  fontWeight={medium}
                  style={{ fontSize: '13px', lineHeight: '13px' }}
                >
                  {`${signedTemplates.filter((item) => item.file_path).length}/${signedTemplates?.length} Documents Complete`}
                </Typography>
              </Box>
            ) : (
              <>
                <ApplicationStatusBadge
                  label={applicationStatusLabel}
                  color={getStatusChipColor(
                    application.withdrawn
                      ? 'withdraw application'
                      : application.employer_archive
                        ? 'archived'
                        : application.application_actions.hired_action == 1
                          ? 'terminated'
                          : application.application_actions.hired_action == 2
                            ? 'resigned'
                            : application.last_action || 'unattended',
                    application,
                  )}
                />
                {applicationStatusLabel === 'hireflix interview submitted' && (
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(
                        application?.hire_flix_interview
                          ?.interview_response_url || '#',
                        '_blank',
                        'noopener,noreferrer',
                      );
                    }}
                    color="lightPurple"
                    size="small"
                    variant="outlined"
                    sx={{
                      width: '100%',
                      maxWidth: '180px',
                      fontSize: '12px',
                      whiteSpace: 'nowrap',
                      mt: 2,
                    }}
                  >
                    View Interview Response
                  </Button>
                )}
              </>
            )}
            {applicationIsHired &&
              isCompany &&
              (companyPlan === 1 || companyPlan === 4) && (
                <Typography
                  style={{
                    color: 'gray',
                    fontSize: '13px',
                    lineHeight: '13px',
                    marginTop: '10px',
                    // whiteSpace: wrapProperty,
                  }}
                >
                  Want to effortlessly onboard your new hire?{' '}
                  <Button
                    onClick={handleNavigation}
                    sx={{
                      fontWeight: 600,
                      color: primaryBlue,
                      display: 'contents',
                      textDecoration: 'underline',
                      fontSize: '13px',
                      minWidth: 'auto',
                      padding: 0,
                    }}
                  >
                    Turn on Digital Onboarding
                  </Button>
                </Typography>
              )}
          </Grid>

          <Grid item xs={12} sm={3} pt={1}>
            <Stack spacing={2} alignItems={{ xs: 'center', sm: 'start' }}>
              {applicationStatusLabel === 'interview confirmed' &&
              dayjs(finalInterviewDate).isBefore(dayjs()) ? (
                <MarkInterviewCTAs
                  application={application}
                  setApplicationData={setApplicationData}
                  setApplication={setSelectedApplication}
                  setIsMessagingModalOpen={setIsMessagingModalOpen}
                  column
                  handleCTAs={handleInterviewRespond}
                />
              ) : applicationStatusLabel === 'HIRED' ? (
                <Stack
                  spacing={2}
                  gap={1}
                  sx={{
                    width: '80%',
                    justifyContent: 'end',
                    alignItems: 'end',
                  }}
                >
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setShowTerminateModal(true);
                    }}
                    size="small"
                    variant="outlined"
                    color="error"
                  >
                    Terminate
                  </Button>
                </Stack>
              ) : (
                <>
                  <Typography
                    color="text.secondary"
                    fontWeight="medium"
                    width="100%"
                    textAlign="center"
                    fontSize="14px"
                  >
                    <ApplicationActionTime
                      application={application}
                      applicationStatusLabel={applicationStatusLabel}
                    />
                  </Typography>

                  {application?.indeed_id ? (
                    <img
                      style={{ width: '40px', alignSelf: 'center' }}
                      src={`${staticFileLocation}img/logos/indeed.png`}
                    />
                  ) : null}
                  {application?.application_source === 4 ? (
                    <img
                      style={{ width: '60px', alignSelf: 'center' }}
                      src={`${staticFileLocation}img/logos/zip-recruiter.jpg`}
                    />
                  ) : null}
                  {application.potential_wotc_credit != null &&
                  Number(application.potential_wotc_credit) > 0 ? (
                    <Typography
                      width="100%"
                      textAlign="center"
                      color={successGreen}
                      fontWeight="medium"
                      fontSize="14px"
                      style={{ marginTop: '5px' }}
                    >
                      {`WOTC $${application.potential_wotc_credit} Credits`}
                    </Typography>
                  ) : null}
                </>
              )}
            </Stack>
          </Grid>
        </Grid>
      </ListItem>
      {isMessagingModalOpen && (
        <MessagingModal
          isOpen={isMessagingModalOpen}
          handleClose={() => setIsMessagingModalOpen(false)}
          application={application}
        />
      )}
      <MarkInterviewModal
        handleClose={() => {
          setSelectedApplication(null);
          setShowInterviewRespondModal('');
        }}
        isOpen={Boolean(showInterviewRespondModal)}
        type={showInterviewRespondModal}
        application={application}
        setApplicationData={setApplicationData}
        setApplication={setSelectedApplication}
        setIsMessagingModalOpen={setIsMessagingModalOpen}
      />
      {showTerminateModal && (
        <EmployerTerminationModal
          isOpen={showTerminateModal}
          handleClose={() => setShowTerminateModal(false)}
          application={application}
          setApplicationData={setApplicationData}
          setApplication={setSelectedApplication}
        />
      )}
    </>
  );
};

export default ApplicationListItem;
